import "@/config";
import "@/bootstrap/network";
console.log("[entry] bundle loaded");
console.info("[build] ts:", Date.now());

// Extend window interface for ingested template storage
declare global {
  interface Window {
    ingestedTemplate?: any;
  }
}

// v2 Canva SDK imports
import { ui, addElementAtPoint, addElementAtCursor, getCurrentPageContext, openDesign } from "@canva/design";
import { upload as uploadAsset } from "@canva/asset";
import { requestOpenExternalUrl, features } from "@canva/platform";
import { auth } from "@canva/user";
import * as exifr from "exifr";

// v2 Template System imports
import { ingestMasterDesignV2 } from "./services/master-ingestion-v2";
import { applyTemplateV2 } from "./services/template-application-v2";
import type { DescriptorV2, ApplicationOptions, IngestionResultV2 } from "./types/descriptor-v2";

// Auto-create design utility
import { createPhotobookPages } from "./utils/auto-create-design";

// Configuration and utilities
import { config, validateConfig, showConfigBanner, getUserKey, CONFIG, apiUrl } from "@/config";
import { canvaFetch, getApiBase } from "@/lib/api";
import {
  createCanvaImageAsset,
  onPhotoPointerDown,
  onPhotoDragStart,
  initializeDragSystem,
  updateFramesIndex,
  isCanvaEnvironment,
  type Photo,
  type DragEvt
} from "@/lib/drag-system";
import "@/styles/drag-system.css";

// Log API base once at startup
console.info("[api] base:", getApiBase());

// Initialize drag system
initializeDragSystem();

// Mount the app UI
function mountApp() {
  // Create root container
  const root = document.createElement("div");
  root.id = "root";
  root.style.display = "flex";
  root.style.flexDirection = "column";
  root.style.height = "100%";

  // Clear any existing content and mount
  document.body.innerHTML = "";
  document.body.appendChild(root);

  // Show config banner if needed
  showConfigBanner();

  // Build the sidebar UI
  buildSidebar(root);
}

// Types for photo management
type PhotoRecord = {
  id: string;
  cdnUrl: string;
  mimeType: string;
  createdAt: string;
  imageRef?: string;
  canvaAssetId?: string;
  canvaAssetRef?: string; // Canva asset reference
  width?: number;
  height?: number;
  name?: string;
  size?: number;
  file?: File; // Original file for SDK upload
};

// Delete photo from backend storage (optional, may fail if credentials not configured)
async function deletePhotoFromStorage(photoId: string): Promise<void> {
  console.log('[photos] Attempting backend storage deletion:', photoId);

  try {
    // Call backend to delete from storage - use same sessionId as upload
    const deleteSessionId = getSessionId(); // Use same session ID as upload
    const deleteUrl = `/api/photos/${encodeURIComponent(photoId)}?sessionId=${encodeURIComponent(deleteSessionId)}`;
    console.log('[photos] DELETE request URL:', deleteUrl);
    console.log('[photos] Session ID (upload-consistent):', deleteSessionId);

    const response = await canvaFetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'X-Session-ID': deleteSessionId
      }
    });

    console.log('[photos] DELETE response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[photos] DELETE failed with error data:', errorData);
      throw new Error(`Backend delete failed: ${response.status} ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('[photos] Backend storage deletion successful:', result);
  } catch (error) {
    // For Canva apps, backend storage deletion is optional
    // The app can function entirely client-side using localStorage
    console.warn('[photos] Backend storage deletion failed (this is expected if storage credentials are not configured):', error.message);
    throw error; // Re-throw to let caller handle gracefully
  }
}

type UploadedAsset = {
  ref: string;
  id: string;
  width: number;
  height: number;
  url: string;
  whenUploaded: () => Promise<void>;
};

type Picked = {
  file: File;
  name: string;
  objectUrl: string;
  date?: Date;
  cdnUrl?: string;
  uploaded?: UploadedAsset;
  status?: "pending" | "uploading" | "uploaded" | "error";
  error?: string;
  dim?: {w:number; h:number};
  photoRecord?: PhotoRecord;
  canvaAssetRef?: string; // Canva asset reference for drag operations
  canvaAssetId?: string; // Canva asset ID
  recentlyUsed?: boolean; // Track if photo was recently used in design
};

// Global state
let photos: PhotoRecord[] = [];
const userKey = getUserKey();

// Session management for Cloudflare Functions with user isolation
async function getSessionId(): Promise<string> {
  try {
    // Get Canva user token to create user-specific session
    const token = await auth.getCanvaUserToken();

    // More robust JWT payload decoding
    let userId: string | null = null;
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1])); // Decode JWT payload
        userId = payload.sub || payload.user_id || payload.id; // Try different user ID fields
      }
    } catch (jwtError) {
      console.warn('[session] JWT decoding failed:', jwtError);
    }

    // Validate userId exists and is valid
    if (!userId || typeof userId !== 'string' || userId.length < 3) {
      throw new Error('Invalid user ID from token');
    }

    // Create user-specific session key
    const userSessionKey = `pb_sessionId_${userId}`;

    // Try URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get('sessionId');

    if (urlSessionId) {
      localStorage.setItem(userSessionKey, urlSessionId);
      return urlSessionId;
    }

    // Try user-specific localStorage
    let sessionId = localStorage.getItem(userSessionKey);

    if (!sessionId) {
      // Generate new user-specific session ID
      const userPrefix = userId.length > 4 ? userId.slice(-4) : userId;
      sessionId = `${userPrefix}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(userSessionKey, sessionId);
    }

    console.log('[session] User-specific session ID:', sessionId, 'for user:', userId.slice(0, 8) + '...');
    return sessionId;
  } catch (error) {
    console.error('[session] Failed to get user-specific session, using fallback:', error);

    // Fallback to simple session management
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get('sessionId');

    if (urlSessionId) {
      localStorage.setItem('pb_sessionId_fallback', urlSessionId);
      return urlSessionId;
    }

    let sessionId = localStorage.getItem('pb_sessionId_fallback');
    if (!sessionId) {
      sessionId = `fallback_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('pb_sessionId_fallback', sessionId);
    }

    console.log('[session] Using fallback session ID:', sessionId);
    return sessionId;
  }
}

// Session ID will be retrieved async when needed

// Photo management functions
async function uploadToBackend(file: File): Promise<{ id: string; url: string; mimeType: string; thumbnailUrl?: string }> {
  const sessionId = await getSessionId();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('sessionId', sessionId);

  console.log('[photos] upload:start', file.name, 'sessionId:', sessionId);

  const response = await canvaFetch('/api/upload', {
    method: 'POST',
    body: formData,
  });

  const result = await response.json();
  console.log('[photos] upload:complete', result.url);
  return result;
}

async function ingestToCanva(url: string, mimeType: string, thumbnailUrl?: string): Promise<UploadedAsset> {
  console.log('[canva] asset:upload:start', url);

  // Validate and normalize MIME type for Canva
  let validMimeType = mimeType;
  const supportedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  if (!supportedTypes.includes(mimeType)) {
    // Try to detect from URL extension
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.png')) {
      validMimeType = 'image/png';
    } else if (urlLower.includes('.gif')) {
      validMimeType = 'image/gif';
    } else if (urlLower.includes('.webp')) {
      validMimeType = 'image/webp';
    } else {
      validMimeType = 'image/jpeg'; // Default fallback
    }
    console.log(`[canva] Fixed MIME type: ${mimeType} -> ${validMimeType}`);
  }

  const asset = await uploadAsset({
    type: "image",
    url: url,
    thumbnailUrl: thumbnailUrl || url,
    mimeType: validMimeType as any,
    aiDisclosure: "none",
  });

  await asset.whenUploaded();
  console.log('[canva] asset:whenUploaded', asset.ref);

  return asset;
}

async function savePhotoRecord(photoRecord: PhotoRecord): Promise<void> {
  // Save to localStorage (photos are now persisted via sessionId in Cloudflare)
  const localPhotos = JSON.parse(localStorage.getItem('photobook:photos') || '[]');
  const existingIndex = localPhotos.findIndex((p: PhotoRecord) => p.id === photoRecord.id);

  if (existingIndex >= 0) {
    localPhotos[existingIndex] = photoRecord;
  } else {
    localPhotos.push(photoRecord);
  }

  localStorage.setItem('photobook:photos', JSON.stringify(localPhotos));
}

// Load photos with explicit backend call + JWT
async function loadPhotos() {
  // Safe guard for auth availability
  if (typeof auth === 'undefined' || !auth.getCanvaUserToken) {
    throw new Error('Auth not available - running without backend');
  }

  const sessionId = await getSessionId();
  const token = await auth.getCanvaUserToken();
  const url = apiUrl(`/api/photos?sessionId=${encodeURIComponent(sessionId)}`);
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  console.debug("[photos] request ->", url, "status:", res.status);
  if (!res.ok) throw new Error(`photos failed: ${res.status}`);
  return res.json();
}

async function loadPhotoRecords(): Promise<PhotoRecord[]> {
  let loadedPhotos: PhotoRecord[] = [];

  try {
    // Load from Cloudflare Functions with sessionId
    const result = await loadPhotos();

    // Convert Cloudflare assets to PhotoRecord format
    loadedPhotos = result.assets.map((asset: any) => {
      // Fix MIME type for old photos
      let mimeType = asset.mimeType;
      if (!mimeType || mimeType === 'application/octet-stream') {
        // Detect MIME type from filename extension
        const filename = asset.id.toLowerCase();
        if (filename.includes('.png')) {
          mimeType = 'image/png';
        } else if (filename.includes('.jpg') || filename.includes('.jpeg')) {
          mimeType = 'image/jpeg';
        } else if (filename.includes('.gif')) {
          mimeType = 'image/gif';
        } else if (filename.includes('.webp')) {
          mimeType = 'image/webp';
        } else {
          mimeType = 'image/jpeg'; // Default fallback
        }
        console.log(`[photos] Fixed MIME type for ${asset.id}: ${mimeType}`);
      }

      return {
        id: asset.id,
        cdnUrl: asset.url,
        mimeType: mimeType,
        createdAt: new Date().toISOString(),
        name: asset.id.replace('bny_', '')
      };
    });
    console.log('[photos] Loaded from Cloudflare:', loadedPhotos.length);
    console.log('[photos] Photo IDs from backend:', loadedPhotos.map(p => p.id));
  } catch (error) {
    console.warn('[photos] Cloudflare load failed, using localStorage fallback:', error);
    const localStoragePhotos = JSON.parse(localStorage.getItem('photobook:photos') || '[]');
    console.log('[photos] localStorage fallback photos:', localStoragePhotos.length);
    console.log('[photos] localStorage photo IDs:', localStoragePhotos.map((p: any) => p.id));
    loadedPhotos = localStoragePhotos;
  }

  return loadedPhotos;
}

async function deletePhotoRecord(id: string): Promise<void> {
  // Note: Cloudflare Functions don't include delete endpoint in this implementation
  // Photos are managed by sessionId namespace in Bunny Storage

  // Delete from localStorage
  const localPhotos = JSON.parse(localStorage.getItem('photobook:photos') || '[]');
  const filteredPhotos = localPhotos.filter((p: PhotoRecord) => p.id !== id);
  localStorage.setItem('photobook:photos', JSON.stringify(filteredPhotos));
}

type Template = { title: string; deeplink: string; thumb?: string };
type CoverTemplate = { id: number; slug: string; name: string; masterDesignId: string };
type CoverTemplateLayer = {
  id: string;
  surface: 'front' | 'spine' | 'back';
  kind: 'text' | 'image' | 'shape';
  props: {
    x: number; y: number; width: number; height: number;
    text?: string; fontSize?: number; fontFamily?: string;
    fontWeight?: string; color?: string; textAlign?: string;
    fill?: string; stroke?: string; strokeWidth?: number;
    imageRef?: string;
  };
  lock: boolean;
  metadata: { userEditable: boolean; alias?: string };
};
type CoverTemplateDescriptor = {
  version: string;
  masterDesignId: string;
  layers: CoverTemplateLayer[];
  createdAt: string;
};

// Legacy templates - only used with ?sample=1 flag
const legacyTemplates: Template[] = [
  { title: "Classic Hardcover",  deeplink: "PASTE_DEEP_LINK_1" },
  { title: "Minimal Softcover",  deeplink: "PASTE_DEEP_LINK_2" },
  { title: "Bold Wedding Cover", deeplink: "PASTE_DEEP_LINK_3" },
];

// Configuration
const USE_SAMPLE_TEMPLATES = new URLSearchParams(window.location.search).get('sample') === '1';

// Real ingestion from current design using Canva SDK
async function ingestCurrentDesign(slug: string, name: string): Promise<any> {
  try {
    console.log(`[ingest] Starting ingestion from current design`);

    // Open the Master design using Canva SDK
    return new Promise((resolve, reject) => {
      openDesign({ type: "current_page" }, async (session) => {
        try {
          console.log(`[ingest] Opened current design for ingestion`);

          // Get page dimensions
          const pageWidth = session.page.width || 2480;
          const pageHeight = session.page.height || 3508;
          console.log(`[ingest] Current page size: ${pageWidth}x${pageHeight}px`);

          // Assume A4 cover size for now (could be configurable)
          const coverSizeMm = { widthMm: 210, heightMm: 297 };
          const sourceScalePxPerMm = pageWidth / coverSizeMm.widthMm;

          // Get all elements from the current design
          const elements = Array.from(session.page.elements);
          console.log(`[ingest] Found ${elements.length} elements in current design`);

          // Debug: Log element details
          if (elements.length > 0) {
            console.log(`[ingest] Element types:`, elements.map(el => el.type));
            console.log(`[ingest] First element:`, elements[0]);
          } else {
            console.log(`[ingest] WARNING: No elements found. Page info:`, {
              pageType: session.page.type,
              pageWidth: session.page.width,
              pageHeight: session.page.height,
              pageLocked: session.page.locked
            });
          }

          // Import ingestion utilities
          const { ingestMasterElements, createCoverDescriptor } = await import('./utils/masterIngestion');

          // Process elements to extract ALL layers (not just aliased ones)
          const ingestionResult = ingestMasterElements(elements, true);

          // Log the ingestion results
          ingestionResult.log.forEach(logLine => console.log(logLine));

          if (ingestionResult.layers.length === 0) {
            reject(new Error(`No elements found in current design. The design appears to be empty.`));
            return;
          }

          // Create complete descriptor
          const descriptor = createCoverDescriptor(
            slug,
            "current-design", // Use placeholder since we can't access actual design ID
            name,
            { widthPx: pageWidth, heightPx: pageHeight },
            coverSizeMm,
            ingestionResult.layers
          );

          console.log(`[ingest] Created descriptor with ${descriptor.layers.length} layers`);
          resolve({
            descriptor,
            log: ingestionResult.log,
            aliasCount: ingestionResult.aliasCount,
            elementCount: ingestionResult.elementCount
          });

        } catch (error) {
          console.error(`[ingest] Failed to process current design:`, error);
          reject(error);
        }
      });
    });

  } catch (error) {
    console.error(`[ingest] Failed to open current design:`, error);
    throw error;
  }
}

// Dev diagnostics: Debug ALT text on current cover page/spread
async function debugCurrentPageAliases(): Promise<void> {
  try {
    console.log('[debug] Starting Alt text analysis on current cover page/spread...');

    await openDesign({ type: "current_page" }, async (session) => {
      const elements = Array.from(session.page.elements);
      console.log(`[debug] Found ${elements.length} elements on current page`);

      // Limit to first ~100 elements as specified
      const elementsToCheck = elements.slice(0, 100);
      if (elements.length > 100) {
        console.log(`[debug] Limiting analysis to first 100 elements (of ${elements.length} total)`);
      }

      const altTexts: string[] = [];
      const aliasResults: Array<{ index: number; altText: string; alias: string | null; surface?: string; elementType: string }> = [];

      // Import alias parser
      const { parseAlias, surfaceOf } = await import('./utils/aliasParser');

      elementsToCheck.forEach((element: any, index: number) => {
        const altText = element.altText || element.alt || '';
        const elementType = element.type || 'unknown';
        altTexts.push(altText);

        console.log(`[debug] ALT[${index}]: ${altText} (${elementType})`);

        if (altText) {
          // Try to parse alias
          const alias = parseAlias(altText);
          const surface = alias ? surfaceOf(alias) : undefined;
          aliasResults.push({ index, altText, alias, surface, elementType });

          if (alias) {
            console.log(`[debug]   -> Parsed alias: ${alias} (surface: ${surface || 'unknown'})`);
          } else {
            console.log(`[debug]   -> No valid alias found`);
          }
        }
      });

      // Detailed summary
      const validAliases = aliasResults.filter(r => r.alias);
      const surfaceGroups = validAliases.reduce((acc, r) => {
        const surface = r.surface || 'unknown';
        if (!acc[surface]) acc[surface] = [];
        acc[surface].push(r.alias);
        return acc;
      }, {} as Record<string, string[]>);

      console.log(`[debug] Summary: ${validAliases.length} valid aliases found out of ${elementsToCheck.length} elements checked`);

      if (Object.keys(surfaceGroups).length > 0) {
        console.log(`[debug] Aliases by surface:`);
        Object.entries(surfaceGroups).forEach(([surface, aliases]) => {
          console.log(`[debug]   ${surface}: [${aliases.join(', ')}]`);
        });
      }

      if (validAliases.length === 0) {
        console.warn('[debug] No aliases found! Use format: alias:front__title, alias:spine__bg, alias:back__photo1');
        alert('No alias found in Alt text (use alias:front__..., spine__..., back__...)\n\nTo add aliases:\n1. Select an element in Master design\n2. Set Alt text to: alias:front__title\n3. Use surface prefixes: front__, spine__, back__\n4. Element types: text, image, shape\n\nCheck console for detailed analysis.');
      } else {
        const aliasNames = validAliases.map(r => r.alias);
        console.log(`[debug] Found ${validAliases.length} aliases: [${aliasNames.join(', ')}]`);

        const surfaceSummary = Object.entries(surfaceGroups)
          .map(([surface, aliases]) => `${surface}: ${aliases.length}`)
          .join(', ');

        alert(`Found ${validAliases.length} valid aliases:\n${aliasNames.join('\n')}\n\nBy surface: ${surfaceSummary}\n\nCheck console for full analysis.`);
      }
    });

  } catch (error) {
    console.error('[debug] Failed to analyze Alt text:', error);
    alert(`Debug failed: ${error.message}`);
  }
}

// Cover template API functions
async function loadCoverTemplates(): Promise<CoverTemplate[]> {
  try {
    const sampleParam = USE_SAMPLE_TEMPLATES ? '?sample=1' : '';
    const response = await canvaFetch(`/api/covers/list${sampleParam}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const templates = await response.json();
    console.log(`[templates] Loaded ${templates.length} templates from backend`);
    return templates;
  } catch (error) {
    console.error('[templates] Failed to load cover templates:', error);
    return [];
  }
}

async function getCoverTemplateDescriptor(slug: string): Promise<CoverTemplateDescriptor | null> {
  try {
    // First, check if we have a locally stored ingested template
    if (window.ingestedTemplate && slug === 'cover-dagz-bfsc-hi') {
      console.log(`[templates] Using locally ingested template with ${window.ingestedTemplate.layers.length} layers`);
      return window.ingestedTemplate;
    }

    // Check localStorage for persisted template
    if (slug === 'cover-dagz-bfsc-hi') {
      try {
        const storedTemplate = localStorage.getItem('ingestedTemplate_cover-dagz-bfsc-hi');
        if (storedTemplate) {
          const parsedTemplate = JSON.parse(storedTemplate);
          console.log(`[templates] Using localStorage template with ${parsedTemplate.layers.length} layers`);
          // Also store in window for this session
          window.ingestedTemplate = parsedTemplate;
          return parsedTemplate;
        }
      } catch (storageError) {
        console.warn(`[templates] Failed to read from localStorage:`, storageError);
      }
    }

    const sampleParam = USE_SAMPLE_TEMPLATES ? '&sample=1' : '';
    const response = await canvaFetch(`/api/covers/get?slug=${slug}${sampleParam}`);

    if (!response.ok) {
      if (response.status === 404) {
        console.error(`[templates] Template descriptor not found for slug: ${slug}`);
        alert(`Template not found: ${slug}\n\nPlease ensure the template has been ingested from a Master design.\n\nTo ingest the DAGz-bFscHI template:\nPOST /api/covers/ingestFromMaster\n{ "masterDesignId": "DAGz-bFscHI", "slug": "cover-dagz-bfsc-hi", "name": "Modern Minimal Cover" }`);
        return null;
      }
      if (response.status === 422) {
        const errorData = await response.json();
        console.error(`[templates] Template ingestion error:`, errorData);
        showToast(`Template ingestion failed: ${errorData.message || 'No aliased elements found'}`, 'error', 6000);
        alert(`Template ingestion failed: ${errorData.message || 'No aliased elements found'}\n\nChecklist:\n1. Open Master design in Canva\n2. Add alias:front__title to Alt text of elements\n3. Use surface prefixes: front__, spine__, back__\n4. Re-run ingestion`);
        return null;
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    console.log(`[templates] Loaded descriptor with ${result.descriptor.layers.length} layers (from backend)`);
    return result.descriptor;
  } catch (error) {
    console.error('[templates] Failed to get template descriptor:', error);
    if (error.message.includes('fetch')) {
      alert(`Network error loading template: ${error.message}\n\nCheck:\n1. Backend API is accessible\n2. CORS configuration\n3. Network connectivity`);
    }
    return null;
  }
}

// Cover spread detection and management
async function getCoverSpreadOrFail(): Promise<any> {
  return new Promise((resolve, reject) => {
    openDesign({ type: "current_page" }, async (session) => {
      try {
        // Check if current page is a cover spread
        if (session.page.type !== "absolute") {
          reject(new Error("Current page is not an absolute layout page"));
          return;
        }

        // For photobook covers, we expect the page to have front/spine/back surfaces
        // This is a simplified check - in a real implementation, we'd verify the page structure
        console.log(`[templates] Found cover spread: ${session.page.width}x${session.page.height}px`);
        resolve(session);
      } catch (error) {
        reject(error);
      }
    });
  });
}

// Remove template-owned layers from cover
async function removeTemplateOwnedCoverLayers(session: any): Promise<void> {
  const elementsToRemove = session.page.elements.filter((element: any) => {
    // Check if element has template metadata
    return element.metadata?.templateOwned === true;
  });

  console.log(`[templates] Removing ${elementsToRemove.length} existing template layers`);

  elementsToRemove.forEach((element: any) => {
    session.page.elements.delete(element);
  });
}

// SDK-based Apply implementation with scale-aware positioning
async function applyCoverTemplate(descriptor: any, opts = { keepUserContent: true }): Promise<void> {
  console.log(`[templates] Applying template: ${descriptor.slug}`);
  console.log(`[templates] Loaded descriptor with ${descriptor.layers.length} layers (from backend)`);

  await openDesign({ type: "current_page" }, async (session) => {
    try {
      // Step 1: Identify cover spread and get current dimensions
      if (session.page.type !== "absolute" || session.page.locked) {
        throw new Error("Cannot apply template to this page type or locked page");
      }

      // Get page dimensions with fallback
      let currentCoverPageWidthPx = session.page.width;
      let currentCoverPageHeightPx = session.page.height;

      // Handle undefined page dimensions (fallback to descriptor size)
      if (!currentCoverPageWidthPx || !currentCoverPageHeightPx) {
        console.warn(`[templates] Page dimensions undefined, using descriptor size as fallback`);
        const sourceScalePxPerMm = descriptor.meta?.sourceScalePxPerMm || 11.81; // ~300 DPI fallback
        currentCoverPageWidthPx = descriptor.coverSize.widthMm * sourceScalePxPerMm;
        currentCoverPageHeightPx = descriptor.coverSize.heightMm * sourceScalePxPerMm;
      }

      console.log(`[templates] Current cover page: ${currentCoverPageWidthPx}x${currentCoverPageHeightPx}px`);

      // Step 2: Use master template scale directly (300 DPI)
      const masterScalePxPerMm = descriptor.meta?.sourceScalePxPerMm || 11.81; // 300 DPI = 11.81 px/mm
      console.log(`[templates] Using master template scale: ${masterScalePxPerMm.toFixed(2)} px/mm (300 DPI)`);
      console.log(`[templates] Master template size: ${descriptor.coverSize.widthMm}x${descriptor.coverSize.heightMm}mm`);

      // Calculate expected canvas size at 300 DPI
      const expectedCanvasWidth = Math.round(descriptor.coverSize.widthMm * masterScalePxPerMm);
      const expectedCanvasHeight = Math.round(descriptor.coverSize.heightMm * masterScalePxPerMm);
      console.log(`[templates] Expected canvas size: ${expectedCanvasWidth}x${expectedCanvasHeight}px`);
      console.log(`[templates] Current canvas size: ${currentCoverPageWidthPx}x${currentCoverPageHeightPx}px`);

      // Step 3: Keep user content if requested
      let userElements: any[] = [];
      if (opts.keepUserContent) {
        userElements = session.page.elements.filter((element: any) =>
          !element.metadata?.templateOwned
        );
      }

      // Step 4: Remove previous template-owned layers
      await removeTemplateOwnedCoverLayers(session);

      // Step 5: Insert each descriptor layer with scale-aware positioning
      for (const layer of descriptor.layers) {
        try {
          console.log(`[templates] Creating layer: ${layer.id} (${layer.kind}) on ${layer.surface}`);

          // Use master template coordinates directly (300 DPI)
          let left = Math.round(layer.props.absXmm * masterScalePxPerMm);
          let top = Math.round(layer.props.absYmm * masterScalePxPerMm);
          const width = Math.round(layer.props.wMm * masterScalePxPerMm);
          const height = Math.round(layer.props.hMm * masterScalePxPerMm);

          // Keep elements within canvas bounds for visibility
          const originalLeft = left;
          const originalTop = top;
          left = Math.max(0, Math.min(left, currentCoverPageWidthPx - width));
          top = Math.max(0, Math.min(top, currentCoverPageHeightPx - height));

          console.log(`[templates] Positioning ${layer.id}: ${layer.props.absXmm.toFixed(1)},${layer.props.absYmm.toFixed(1)}mm -> ${originalLeft},${originalTop}px -> adjusted to ${left},${top}px (${width}x${height}px)`);
          // Use minimal properties to avoid sync errors
          const rotation = 0; // Disable rotation for now
          const transparency = 0; // Disable transparency for now

          // Validate all positioning values are finite
          if (!isFinite(left) || !isFinite(top) || !isFinite(width) || !isFinite(height)) {
            throw new Error(`Invalid element positioning: left=${left}, top=${top}, width=${width}, height=${height}`);
          }

          // Ensure minimum dimensions
          const minWidth = Math.max(width, 1);
          const minHeight = Math.max(height, 1);

          let element: any = null;

          // Create element using direct API with correct format
          let elementData: any = null;

          switch (layer.kind) {
            case 'text':
              // Create text element with proper children structure
              elementData = {
                type: "text",
                left,
                top,
                width: minWidth,
                children: [layer.props.text || 'Sample Text']
              };
              break;

            case 'image':
              // Create rectangle as image placeholder
              console.log(`[templates] Creating image placeholder for ${layer.id}`);
              elementData = {
                type: "rectangle",
                left,
                top,
                width: minWidth,
                height: minHeight
              };
              break;

            case 'shape':
              // Create rectangle shape
              console.log(`[templates] Creating minimal rectangle shape for ${layer.id}`);
              elementData = {
                type: "rectangle",
                left,
                top,
                width: minWidth,
                height: minHeight
              };
              break;
          }

          if (elementData) {
            try {
              // Add element using direct API methods to bypass session sync issues
              await addElementAtPoint(elementData);
              console.log(`[templates] Added ${layer.kind} element: ${layer.id} at ${layer.props.absXmm.toFixed(1)},${layer.props.absYmm.toFixed(1)}mm`);
            } catch (pointError) {
              console.log(`[templates] Point method failed, trying cursor method for ${layer.id}`);
              try {
                await addElementAtCursor(elementData);
                console.log(`[templates] Added ${layer.kind} element: ${layer.id} at cursor`);
              } catch (cursorError) {
                console.error(`[templates] Failed to add element ${layer.id} with both methods:`, cursorError);
              }
            }
          }

        } catch (error) {
          console.error(`[templates] Failed to create element for layer ${layer.id}:`, error);
        }
      }

      // Step 6: Raise user content above template layers
      if (opts.keepUserContent) {
        userElements.forEach((element: any) => {
          try {
            // Use the correct Canva API method to move elements to front
            element.moveToFront();
          } catch (moveError) {
            console.warn(`[templates] Failed to move element to front:`, moveError);
          }
        });
      }

      // Step 7: Save doc metadata
      // Note: This would require document-level metadata storage
      console.log(`[templates] Setting active template: ${descriptor.slug}`);

      // Step 8: No sync needed since we used direct API calls
      console.log(`[templates] Applied template ${descriptor.slug} successfully using direct API (${descriptor.layers.length} elements)`);

      // Show success toast
      showToast(`Applied template "${descriptor.slug}" with ${descriptor.layers.length} layers`, 'success');

    } catch (error) {
      console.error(`[templates] Failed to apply template in session:`, error);
      showToast(`Failed to apply template: ${error.message}`, 'error');
      throw error;
    }
  });
}

// Wrapper function for backward compatibility
async function applyTemplate(slug: string): Promise<void> {
  try {
    console.log(`[templates] Applying template: ${slug}`);

    // Get template descriptor
    const descriptor = await getCoverTemplateDescriptor(slug);
    if (!descriptor) {
      console.error('[templates] Template descriptor not found');
      alert('Template descriptor not found. Please ensure the template has been ingested.');
      return;
    }

    // Use the new SDK-based apply function
    await applyCoverTemplate(descriptor, { keepUserContent: true });

  } catch (error) {
    console.error('[templates] Failed to apply template:', error);
    // Show user-friendly error message
    alert(`Failed to apply template: ${error.message}`);
  }
}

// Function to deeply clean objects of circular references
function deepCleanObject(obj: any, seen = new WeakSet()): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (seen.has(obj)) {
    return '[Circular Reference]';
  }

  seen.add(obj);

  if (Array.isArray(obj)) {
    return obj.map(item => deepCleanObject(item, seen));
  }

  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    // Skip problematic properties that might contain circular references
    if (key === 'session' || key === 'element' || key === 'page' ||
        key === 'parent' || key === 'children' || key === 'owner' ||
        typeof value === 'function') {
      continue;
    }

    try {
      cleaned[key] = deepCleanObject(value, seen);
    } catch (error) {
      // If we can't clean this property, skip it
      console.warn(`[clean] Skipping property ${key} due to error:`, error);
    }
  }

  seen.delete(obj);
  return cleaned;
}

// Debug function to list current page elements
async function debugCurrentPageElements(): Promise<void> {
  try {
    console.log(`[debug] Checking current page elements`);

    return new Promise((resolve, reject) => {
      openDesign({ type: "current_page" }, async (session) => {
        try {
          console.log(`[debug] Opened current page for debugging`);

          // Get page info
          console.log(`[debug] Page info:`, {
            type: session.page.type,
            width: session.page.width,
            height: session.page.height,
            locked: session.page.locked
          });

          // Get all elements
          const elements = Array.from(session.page.elements);
          console.log(`[debug] Found ${elements.length} elements`);

          if (elements.length > 0) {
            elements.forEach((element, index) => {
              console.log(`[debug] Element ${index}:`, {
                type: element.type,
                id: element.id,
                altText: element.altText || 'none',
                bounds: {
                  left: element.left,
                  top: element.top,
                  width: element.width,
                  height: element.height
                }
              });
            });
          } else {
            console.log(`[debug] No elements found on current page`);
          }

          resolve();
        } catch (error) {
          console.error(`[debug] Error:`, error);
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error(`[debug] Failed to debug page:`, error);
    throw error;
  }
}

// Function to trigger current design ingestion from frontend
async function triggerCurrentDesignIngestion(slug: string, name: string): Promise<void> {
  try {
    console.log(`[ingest] Triggering current design ingestion`);
    showToast(`Starting ingestion from current design...`, 'info');

    // Perform ingestion from current design using Canva SDK
    const ingestionResult = await ingestCurrentDesign(slug, name);

    // Clean the descriptor to remove circular references
    console.log(`[ingest] Cleaning descriptor for transmission...`);
    const cleanDescriptor = deepCleanObject(ingestionResult.descriptor);
    console.log(`[ingest] Cleaned descriptor successfully`);

    // Send the real descriptor to backend
    const response = await canvaFetch('/api/covers/ingestFromMaster', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        masterDesignId: "current-design",
        slug,
        name,
        realDescriptor: cleanDescriptor,
        ingestionLog: ingestionResult.log
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    const result = await response.json();
    console.log(`[ingest] Successfully ingested current design:`, result);
    const elementCount = ingestionResult?.layers?.length || result?.aliasCount || 0;

    // Store the ingested template data locally for immediate use
    if (result.descriptor && result.descriptor.layers) {
      console.log(`[ingest] Storing ingested template locally with ${result.descriptor.layers.length} layers`);
      // Update the template in our local cache
      window.ingestedTemplate = result.descriptor;

      // Also store in localStorage for persistence across browser sessions
      try {
        localStorage.setItem('ingestedTemplate_cover-dagz-bfsc-hi', JSON.stringify(result.descriptor));
        console.log(`[ingest] Stored template in localStorage for persistence`);
      } catch (storageError) {
        console.warn(`[ingest] Failed to store in localStorage:`, storageError);
      }
    }

    showToast(`Successfully ingested ${elementCount} elements from current design`, 'success', 6000);

  } catch (error) {
    console.error(`[ingest] Failed to ingest current design:`, error);
    showToast(`Failed to ingest design: ${error.message}`, 'error', 8000);
    throw error;
  }
}

// ============ V2 Template System ============

/**
 * Ingest a Master design using the v2 full-fidelity system
 */
async function ingestMasterV2(masterDesignId: string, slug: string, name: string, captureMode: 'aliased' | 'all' = 'all'): Promise<void> {
  console.log(`[ingest-v2] Starting v2 ingestion: ${masterDesignId} -> ${slug}`);

  try {
    // Show progress
    showToast(`Starting v2 ingestion of ${name}...`, 'info', 3000);

    // Perform ingestion
    const result: IngestionResultV2 = await ingestMasterDesignV2(masterDesignId, slug, name, captureMode);

    console.log(`[ingest-v2] Ingestion complete:`, result.stats);
    console.log(`[ingest-v2] Log:`, result.log);

    // Save to backend
    const saveResponse = await canvaFetch('/api/templates/v2/ingest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        masterDesignId,
        slug,
        name,
        captureMode,
        descriptor: result.descriptor,
        log: result.log,
        stats: result.stats
      })
    });

    if (!saveResponse.ok) {
      throw new Error(`Failed to save v2 template: ${saveResponse.statusText}`);
    }

    const saveResult = await saveResponse.json();
    console.log(`[ingest-v2] Saved to backend:`, saveResult);

    // Show success with stats
    const statsText = `${result.stats.totalElements} elements, ${result.stats.groupsCount} groups, ${result.stats.imagesCount} images`;
    showToast(`✅ V2 ingestion complete: ${statsText}`, 'success', 5000);

    // Note: Template list will refresh automatically on next load

  } catch (error) {
    console.error(`[ingest-v2] Failed:`, error);
    showToast(`❌ V2 ingestion failed: ${error.message}`, 'error', 8000);
    throw error;
  }
}

/**
 * Apply a v2 template with full-fidelity reconstruction
 */
async function applyTemplateV2Wrapper(slug: string, options: ApplicationOptions = {
  mode: 'exact',
  keepUserContent: true,
  createNewPage: false,
  assetStrategy: 'auto'
}): Promise<void> {
  console.log(`[apply-v2] Starting v2 application: ${slug}`);

  try {
    // Show progress
    showToast(`Loading v2 template ${slug}...`, 'info', 2000);

    // Get v2 descriptor from backend
    const response = await canvaFetch(`/api/templates/v2/${encodeURIComponent(slug)}`);
    if (!response.ok) {
      throw new Error(`Failed to load v2 template: ${response.statusText}`);
    }

    const templateData = await response.json();
    if (!templateData.descriptorJson) {
      throw new Error('No v2 descriptor found for template');
    }

    const descriptor: DescriptorV2 = JSON.parse(templateData.descriptorJson);
    console.log(`[apply-v2] Loaded descriptor v${descriptor.version}:`, descriptor.meta);

    // Apply template
    showToast(`Applying ${descriptor.layers.length} elements...`, 'info', 3000);
    const result = await applyTemplateV2(descriptor, options);

    console.log(`[apply-v2] Application result:`, result);

    // Output detailed application logs for debugging
    console.log(`[apply-v2] Detailed logs:`, result.log);

    if (result.success) {
      const statsText = `${result.elementsCreated} elements, ${result.assetsReused} assets reused, ${result.assetsUploaded} uploaded`;
      showToast(`✅ V2 template applied: ${statsText}`, 'success', 5000);
    } else {
      const errorText = result.errors.join(', ');
      showToast(`⚠️ V2 template applied with errors: ${errorText}`, 'warning', 8000);
    }

  } catch (error) {
    console.error(`[apply-v2] Failed:`, error);
    showToast(`❌ V2 application failed: ${error.message}`, 'error', 8000);
    throw error;
  }
}

/**
 * Get v2 template diagnostics and comparison data
 */
async function getTemplateV2Diagnostics(slug: string): Promise<void> {
  try {
    const response = await canvaFetch(`/api/templates/v2/${encodeURIComponent(slug)}/diagnostics`);
    if (!response.ok) {
      throw new Error(`Failed to get diagnostics: ${response.statusText}`);
    }

    const diagnostics = await response.json();
    console.log(`[diagnostics-v2] Template ${slug}:`, diagnostics);

    // Show diagnostics in a modal or console
    const info = [
      `Descriptor Version: ${diagnostics.descriptorVersion}`,
      `Elements: ${diagnostics.stats.totalElements}`,
      `Groups: ${diagnostics.stats.groupsCount}`,
      `Images: ${diagnostics.stats.imagesCount}`,
      `Fonts: ${diagnostics.stats.fontsCount}`,
      `Aliased Elements: ${diagnostics.stats.aliasedElements}`,
      `Source Size: ${diagnostics.meta.sourcePageSizePx.widthPx}x${diagnostics.meta.sourcePageSizePx.heightPx}px`,
      `Scale: ${diagnostics.meta.sourceScalePxPerMm.toFixed(2)} px/mm`
    ].join('\n');

    alert(`V2 Template Diagnostics:\n\n${info}`);

  } catch (error) {
    console.error(`[diagnostics-v2] Failed:`, error);
    showToast(`Failed to get diagnostics: ${error.message}`, 'error', 5000);
  }
}

// Legacy function removed - now using SDK-based applyCoverTemplate

const TAB_KEYS = ["templates","layouts","photos","export"] as const;
type TabKey = typeof TAB_KEYS[number];
const STORAGE_KEY = "pb_active_tab_v1";

/* ============ Styles ============ */
function injectStyles() {
  const css = `
  :root { --fg:#0f172a; --muted:#64748b; --line:#e5e7eb; --card:#fff; --accent:#111827; --radius:12px;
          --shadow:0 1px 3px rgba(0,0,0,.08), 0 6px 20px rgba(0,0,0,.06);
          --shadow-lg:0 10px 25px rgba(0,0,0,.14); }
  *{box-sizing:border-box}
  body{background:transparent;color:var(--fg)}
  .pb-root{font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;padding:10px;display:grid;grid-template-rows:auto auto 1fr auto;gap:10px;min-height:100vh}
  h2{margin:0 0 6px 0;font-size:18px}
  p{margin:0;color:var(--muted);font-size:13px}
  /* Tabs */
  .tabs{display:flex;gap:6px;background:#fff;position:sticky;top:0;z-index:5}
  .tab-btn{flex:1 1 auto;border:1px solid var(--line);background:#fff;border-radius:10px;padding:8px 10px;font-weight:600;font-size:12px;cursor:pointer}
  .tab-btn[aria-selected="true"]{background:#111;color:#fff;border-color:#111}
  /* Shared blocks */
  .section-title{font-weight:700;font-size:13px;margin:6px 0}
  .btn{appearance:none;border:1px solid var(--accent);background:var(--accent);color:#fff;padding:10px 12px;border-radius:10px;font-weight:600;font-size:13px;cursor:pointer}
  .btn.outline{background:#fff;color:var(--accent)}
  .meta{font-size:12px;color:#334155}
  /* Template grid */
  .grid{display:grid;gap:8px;grid-template-columns:repeat(2,minmax(0,1fr));align-content:start}
  .card{border:1px solid var(--line);border-radius:var(--radius);background:#fff;box-shadow:var(--shadow);overflow:hidden;cursor:pointer;transition:transform .16s,box-shadow .16s,border-color .16s}
  .card:hover{transform:translateY(-2px);box-shadow:var(--shadow-lg);border-color:#d1d5db}
  .thumb{aspect-ratio:1/1;display:grid;place-items:center;background:linear-gradient(180deg,#f9fafb,#f3f4f6);color:var(--muted);font-size:12px}
  .thumb img{width:100%;height:100%;object-fit:cover;display:block}
  .title{padding:8px 10px;font-weight:600;font-size:13px}
  .apply-btn{position:absolute;bottom:8px;right:8px;padding:4px 8px;font-size:11px;background:var(--accent);color:white;border:none;border-radius:4px;cursor:pointer;font-weight:600}
  .apply-btn:hover{background:#2563eb}
  /* V2 Controls */
  .v2-controls{display:flex;gap:6px;align-items:center}
  .v2-controls .btn{padding:6px 10px;font-size:11px}
  .button-container{display:flex;gap:4px;position:absolute;bottom:8px;right:8px}
  .button-container .apply-btn{position:static;margin:0}
  .apply-btn.primary{background:#059669;border-color:#059669}
  .apply-btn.primary:hover{background:#047857}
  .apply-btn.secondary{background:#6b7280;border-color:#6b7280}
  .apply-btn.secondary:hover{background:#4b5563}
  .loading{padding:20px;text-align:center;color:var(--muted);font-size:13px}
  .empty-state{padding:20px;text-align:center;color:var(--muted);font-size:13px}
  .error{padding:20px;text-align:center;color:#dc2626;font-size:13px}
  /* Photos */
  .uploader{border:1px dashed var(--line);border-radius:12px;padding:10px;display:grid;gap:8px;background:#fafafa}
  .drop{border:1px dashed var(--line);border-radius:10px;padding:16px;text-align:center;color:var(--muted);background:#fff}
  .photo-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;align-content:start}
  .photo-card{border:1px solid var(--line);border-radius:10px;overflow:hidden;background:#fff;box-shadow:var(--shadow);cursor:grab;position:relative;user-select:none;-webkit-user-drag:none}
  .photo-card:active{cursor:grabbing}
  .photo-card img{width:100%;height:120px;object-fit:cover;display:block;background:#f3f4f6}
  .photo-card .status, .photo-card .overlay{pointer-events:none}
  .photo-card .actions a{pointer-events:auto}
  .photo-card .caption{padding:6px 8px;font-size:12px;color:#334155;white-space:nowrap;text-overflow:ellipsis;overflow:hidden}
  .card-controls{position:absolute;top:4px;right:4px;display:flex;gap:4px;align-items:center;z-index:20;pointer-events:auto}
  .delete-btn{width:20px;height:20px;border:none;border-radius:50%;background:rgba(220,53,69,0.9);color:white;font-size:14px;font-weight:bold;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all 0.2s ease;z-index:30;pointer-events:auto}
  .delete-btn:hover{background:rgba(220,53,69,1);transform:scale(1.1)}
  .delete-btn:disabled{background:rgba(108,117,125,0.9);cursor:not-allowed;transform:none}
  .cdn-link{width:20px;height:20px;border:none;border-radius:50%;background:rgba(13,110,253,0.9);color:white;font-size:12px;text-decoration:none;display:flex;align-items:center;justify-content:center;transition:all 0.2s ease;z-index:30;pointer-events:auto}
  .cdn-link:hover{background:rgba(13,110,253,1);transform:scale(1.1)}
  .status{position:absolute;top:6px;left:6px;padding:2px 6px;border-radius:8px;font-size:10px;background:#f1f5f9;color:#0f172a}
  .status.uploading{background:#fff3cd}
  .status.uploaded{background:#dcfce7}
  .status.error{background:#fee2e2}
  .debug-area{margin:8px 0;padding:8px;background:#f8f9fa;border:1px solid #e5e7eb;border-radius:6px;font-size:12px}
  .btn-small{padding:4px 8px;font-size:11px;border:1px solid #d1d5db;border-radius:4px;background:#fff;cursor:pointer}
  .btn-small:hover{background:#f3f4f6}
  .btn-small:disabled{opacity:0.5;cursor:not-allowed}
  .verbose-toggle{margin-left:8px;font-size:11px;cursor:pointer}
  .card-controls{position:absolute;top:6px;right:6px;display:flex;gap:4px;align-items:center}
  .cdn-link{display:inline-block;width:16px;height:16px;background:#3b82f6;color:white;text-align:center;line-height:16px;border-radius:3px;text-decoration:none;font-size:10px}
  .cdn-link:hover{background:#2563eb}
  .error-text{font-size:9px;color:#dc2626;max-width:100px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  /* Footer */
  .pb-footer{position:sticky;bottom:0;background:#fff;border-top:1px solid var(--line);padding-top:6px}
  .row{display:flex;gap:8px;flex-wrap:wrap}
  `;
  const s = document.createElement("style"); s.textContent = css; document.head.appendChild(s);
}

/* ============ Helpers ============ */
function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> = {}, children: (HTMLElement|string)[] = []) {
  const node = document.createElement(tag);
  Object.assign(node, props);
  for (const c of children) node.append(c);
  return node;
}
function formatDate(d?: Date){ if(!d) return "no EXIF date"; const y=d.getFullYear(), m=String(d.getMonth()+1).padStart(2,"0"), day=String(d.getDate()).padStart(2,"0"); return `${y}-${m}-${day}`; }

// Global state for verbose logging
let verboseLogs = false;

async function getImageSizeFromObjectUrl(url: string): Promise<{ width:number; height:number }> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res({ width: img.naturalWidth || 1000, height: img.naturalHeight || 1000 });
    img.onerror = () => {
      console.warn("Failed to load image for size detection, using fallback 1000x1000");
      res({ width: 1000, height: 1000 });
    };
    img.src = url;
  });
}

function fitPreviewSize(w:number,h:number,box=160){ const r=Math.max(w,h); const s=box/r; return { width:Math.round(w*s), height:Math.round(h*s) }; }

// Removed unused startCanvaDrag function - drag is handled directly in pointerdown event

function fitBox(w:number, h:number, box=160){
  const s = box / Math.max(w, h);
  return { width: Math.max(1, Math.round(w*s)), height: Math.max(1, Math.round(h*s)) };
}

// Measure image dimensions from objectUrl
async function measureImage(objectUrl: string): Promise<{w:number;h:number}> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res({ w: img.naturalWidth || 1600, h: img.naturalHeight || 1200 });
    img.onerror = rej;
    img.src = objectUrl;
  });
}

// Simple inline hint function
function showInlineHint(message: string) {
  console.log("hint:", message);
  // Could add a temporary UI hint here if needed
}

// Toast notification utility
function showToast(message: string, type: 'success' | 'error' | 'info' = 'info', duration = 4000) {
  const toast = document.createElement('div');
  const bgColor = type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6';

  toast.style.cssText = `
    position: fixed;
    top: 16px;
    right: 16px;
    background: ${bgColor};
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    z-index: 10000;
    max-width: 300px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    animation: slideIn 0.3s ease-out;
  `;

  // Add slide-in animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);

  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    if (toast.parentNode) {
      toast.style.animation = 'slideIn 0.3s ease-out reverse';
      setTimeout(() => toast.parentNode?.removeChild(toast), 300);
    }
  }, duration);
}

// Create a custom confirmation dialog (since confirm() is blocked in Canva's sandboxed environment)
function createConfirmationDialog(
  title: string,
  message: string,
  onConfirm: () => void,
  onCancel: () => void
): HTMLElement {
  // Create overlay
  const overlay = el("div", {
    style: `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
    `
  });

  // Create dialog
  const dialog = el("div", {
    style: `
      background: white;
      border-radius: 8px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    `
  });

  // Title
  const titleEl = el("h3", {
    innerText: title,
    style: `
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #1f2937;
    `
  });

  // Message
  const messageEl = el("p", {
    innerText: message,
    style: `
      margin: 0 0 20px 0;
      font-size: 14px;
      color: #6b7280;
      line-height: 1.4;
    `
  });

  // Buttons container
  const buttons = el("div", {
    style: `
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    `
  });

  // Cancel button
  const cancelBtn = el("button", {
    innerText: "Cancel",
    style: `
      padding: 8px 16px;
      border: 1px solid #d1d5db;
      background: white;
      color: #374151;
      border-radius: 6px;
      font-size: 14px;
      cursor: pointer;
      font-weight: 500;
    `
  }) as HTMLButtonElement;

  // Confirm button
  const confirmBtn = el("button", {
    innerText: "Delete",
    style: `
      padding: 8px 16px;
      border: none;
      background: #dc2626;
      color: white;
      border-radius: 6px;
      font-size: 14px;
      cursor: pointer;
      font-weight: 500;
    `
  }) as HTMLButtonElement;

  // Event handlers
  const closeDialog = () => {
    overlay.remove();
  };

  cancelBtn.onclick = () => {
    closeDialog();
    onCancel();
  };

  confirmBtn.onclick = () => {
    closeDialog();
    onConfirm();
  };

  // Close on overlay click
  overlay.onclick = (e) => {
    if (e.target === overlay) {
      closeDialog();
      onCancel();
    }
  };

  // Close on Escape key
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeDialog();
      onCancel();
      document.removeEventListener('keydown', handleKeydown);
    }
  };
  document.addEventListener('keydown', handleKeydown);

  // Assemble dialog
  buttons.append(cancelBtn, confirmBtn);
  dialog.append(titleEl, messageEl, buttons);
  overlay.append(dialog);

  return overlay;
}

// Global redraw registry for UI updates
let globalRedrawFn: (() => void) | null = null;

function registerRedrawFunction(fn: () => void) {
  globalRedrawFn = fn;

  // If photos were already loaded but UI wasn't updated, trigger redraw now
  if (photosInitialized && photos.length > 0) {
    console.log('[photos] Redraw function registered, updating UI with existing photos...');
    triggerGlobalRedraw();
  }
}

function triggerGlobalRedraw() {
  if (globalRedrawFn) {
    globalRedrawFn();
  }
}

// Initialize photos on app load
let photosInitialized = false;
async function initializePhotos() {
  if (photosInitialized) {
    console.log('[photos] Already initialized, skipping...');
    return;
  }

  try {
    console.log('[photos] Loading persisted photos...');
    photosInitialized = true;
    photos = await loadPhotoRecords();
    console.log(`[photos] Loaded ${photos.length} photos`);

    // Convert to picked format for UI (prevent duplicates)
    for (const photo of photos) {
      // Check if photo already exists in picked array
      const existingIndex = picked.findIndex(p => p.photoRecord?.id === photo.id);
      if (existingIndex >= 0) {
        console.log('[photos] Photo already exists in picked array:', photo.id);
        continue;
      }

      const pickedItem: Picked = {
        file: new File([], photo.name || 'image.jpg'), // Empty file for backend photos
        name: photo.name || 'image.jpg',
        objectUrl: photo.cdnUrl,
        cdnUrl: photo.cdnUrl,
        status: "uploaded",
        photoRecord: photo,
        // Set Canva asset references from backend data
        canvaAssetRef: photo.imageRef || photo.canvaAssetRef,
        canvaAssetId: photo.canvaAssetId,
        // Only create uploaded object if we have a real Canva asset reference
        uploaded: photo.imageRef ? {
          ref: photo.imageRef,
          id: photo.canvaAssetId || photo.id,
          width: photo.width || 200,
          height: photo.height || 200,
          url: photo.cdnUrl,
          whenUploaded: async () => {}
        } : undefined
      };

      picked.push(pickedItem);
    }

    // Trigger UI update
    triggerGlobalRedraw();
  } catch (error) {
    console.error('[photos] Failed to initialize photos:', error);
    photosInitialized = false; // Reset flag on error so retry is possible
  }
}







// WeakMap to prevent duplicate uploads
const uploadingMap = new WeakMap<File, Promise<UploadedAsset>>();

async function ensureUploaded(p: Picked): Promise<UploadedAsset> {
  if (p.uploaded) return p.uploaded;

  // For existing photos with CDN URLs, create real Canva assets
  if (p.cdnUrl && p.photoRecord && !p.photoRecord.imageRef) {
    console.log('[photos] Creating Canva asset for existing photo:', p.name);
    p.status = "uploading";

    try {
      // Fetch the image from CDN and create a real Canva asset
      const response = await fetch(p.cdnUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }
      const blob = await response.blob();

      // Get image dimensions
      const wh = await getImageSizeFromObjectUrl(p.cdnUrl);

      // Create real Canva asset using the blob
      const asset = await createCanvaImageAsset({
        file: blob,
        title: p.name,
        width: wh.width,
        height: wh.height
      });

      // Create uploaded object with real Canva asset reference
      p.uploaded = {
        ref: asset.assetRef,
        id: asset.assetId,
        width: wh.width,
        height: wh.height,
        url: p.cdnUrl,
        whenUploaded: async () => {}
      };

      // Update photo record with Canva asset reference
      p.photoRecord.imageRef = asset.assetRef;
      p.photoRecord.canvaAssetId = asset.assetId;
      p.photoRecord.width = wh.width;
      p.photoRecord.height = wh.height;

      // Save updated photo record
      await savePhotoRecord(p.photoRecord);

      p.status = "uploaded";
      console.log('[photos] Created Canva asset for existing photo:', asset.assetRef);
      return p.uploaded;
    } catch (error) {
      console.error('[photos] Failed to create Canva asset for existing photo:', error);
      p.status = "error";
      p.error = error instanceof Error ? error.message : String(error);
      throw error;
    }
  }

  // Check if already uploading to prevent duplicates
  const existingUpload = uploadingMap.get(p.file);
  if (existingUpload) return existingUpload;

  p.status = "uploading";

  const uploadPromise = (async () => {
    try {
      // Get image dimensions
      const wh = await getImageSizeFromObjectUrl(p.objectUrl);

      // Upload to backend (Bunny Storage via Cloudflare Functions)
      const { id, url, mimeType, thumbnailUrl } = await uploadToBackend(p.file);
      p.cdnUrl = url;

      // Ingest into Canva
      const asset = await ingestToCanva(url, mimeType, thumbnailUrl);
      p.uploaded = asset;
      p.status = "uploaded";

      // Create photo record and save
      if (!p.photoRecord) {
        const photoRecord: PhotoRecord = {
          id: id,
          cdnUrl: url,
          mimeType,
          createdAt: new Date().toISOString(),
          imageRef: asset.ref,
          canvaAssetId: asset.id,
          width: wh.width,
          height: wh.height,
          name: p.name
        };

        p.photoRecord = photoRecord;
        await savePhotoRecord(photoRecord);
        console.log('[photos] Saved photo record:', photoRecord.id);
      }

      return asset;
    } catch (e: any) {
      p.status = "error";
      p.error = e?.message || String(e);
      console.error("[photos] upload:error", p.name, e);
      throw e;
    } finally {
      uploadingMap.delete(p.file);
    }
  })();

  uploadingMap.set(p.file, uploadPromise);
  return uploadPromise;
}

function layoutPreviewSVG(preset: LayoutPreset, size = 160): string {
  const pad = 8;
  const inner = size - pad*2;
  const rects = preset.frames.map((f) => {
    const x = pad + f.x * inner;
    const y = pad + f.y * inner;
    const w = f.w * inner;
    const h = f.h * inner;
    const r = 10;
    if (f.shape === "circle") {
      const cx = x + w/2, cy = y + h/2, rx = w/2, ry = h/2;
      return `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="#f3f4f6" stroke="#cbd5e1" stroke-width="1"/>`;
    }
    return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" ry="${r}" fill="#f3f4f6" stroke="#cbd5e1" stroke-width="1"/>`;
  }).join("");
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg" style="display:block;background:#fff;border:1px solid #e5e7eb;border-radius:12px">${rects}</svg>`;
}

// Convert normalized 0..1 to absolute px using page dimensions
const px = (n: number, dim: number) => Math.round(n * dim);

// Try to read the current page size. Fall back to a safe nominal size.
async function getPagePx(): Promise<{ width: number; height: number }> {
  try {
    const ctx: any = await getCurrentPageContext();
    const w = ctx?.dimensions?.width;
    const h = ctx?.dimensions?.height;
    if (typeof w === "number" && typeof h === "number" && w > 0 && h > 0) {
      return { width: w, height: h };
    }
  } catch {}
  return { width: 2000, height: 2000 };
}

// Build a REAL Canva frame element (shape with dropTarget fill) at absolute px
function buildFrameElementPx(spec: FrameSpec, page: { width: number; height: number }) {
  const left   = px(spec.x, page.width);
  const top    = px(spec.y, page.height);
  const width  = px(spec.w, page.width);
  const height = px(spec.h, page.height);

  // SHAPE path: rectangle or ellipse in a local 100x100 viewBox
  const viewBox = { left: 0, top: 0, width: 100, height: 100 };
  let paths: any[] = [];
  if (spec.shape === "circle") {
    // Use an ellipse path that fills the 100x100 box
    paths = [{ d: "M 50 0 A 50 50 0 1 0 50.001 0 Z", fill: { dropTarget: true } }];
  } else {
    // Rectangle
    paths = [{ d: "M 0 0 H 100 V 100 H 0 Z", fill: { dropTarget: true } }];
  }

  // This 'shape' will be treated as a FRAME because fill.dropTarget = true and no color/asset is set
  const element: any = {
    type: "shape",
    viewBox,
    paths,
    width,
    height,
    left,
    top,
    rotation: 0
  };
  return element;
}

// Insert a frame element at a point/cursor (handles fixed vs responsive designs)
async function insertFrameElement(el: any) {
  try {
    const result = await addElementAtPoint(el);   // Works for "fixed" designs (Presentations, etc.)
    console.log("[frames] Added frame at point:", result);
    return result;
  } catch (e) {
    console.log("[frames] Fallback to cursor placement");
    const result = await addElementAtCursor(el);  // Fallback for responsive designs (Docs, etc.)
    console.log("[frames] Added frame at cursor:", result);
    return result;
  }
}

/* ============ Shared state ============ */
const picked: Picked[] = [];

// Each frame is normalized to page size (0..1). shape can be 'rect' or 'circle'.
type FrameSpec = { x: number; y: number; w: number; h: number; shape?: "rect" | "circle" };
type LayoutPreset = { id: string; title: string; ratio?: string; frames: FrameSpec[] };

const LAYOUTS: LayoutPreset[] = [
  { id: "one-full", title: "1 photo (full)", frames: [
    { x: 0.05, y: 0.05, w: 0.90, h: 0.90, shape: "rect" }
  ]},
  { id: "two-50-50", title: "2 photos (50/50)", frames: [
    { x: 0.05, y: 0.10, w: 0.425, h: 0.80, shape: "rect" },
    { x: 0.525, y: 0.10, w: 0.425, h: 0.80, shape: "rect" }
  ]},
  { id: "three-1-2-grid", title: "3 photos (1+2 grid)", frames: [
    { x: 0.05, y: 0.10, w: 0.90, h: 0.40, shape: "rect" },
    { x: 0.05, y: 0.55, w: 0.425, h: 0.35, shape: "rect" },
    { x: 0.525, y: 0.55, w: 0.425, h: 0.35, shape: "rect" }
  ]},
  { id: "four-2x2", title: "4 photos (2×2)", frames: [
    { x: 0.05, y: 0.08, w: 0.425, h: 0.40, shape: "rect" },
    { x: 0.525, y: 0.08, w: 0.425, h: 0.40, shape: "rect" },
    { x: 0.05, y: 0.52, w: 0.425, h: 0.40, shape: "rect" },
    { x: 0.525, y: 0.52, w: 0.425, h: 0.40, shape: "rect" }
  ]}
];

/* ============ Templates view ============ */
function TemplatesView(): HTMLElement {
  const wrap = el("div");

  // Header with title and debug button
  const header = el("div", { className: "section-header" });
  const title = el("div", { className: "section-title", innerText: "Cover templates" });
  header.append(title);

  // Add debug button in development mode
  if (USE_SAMPLE_TEMPLATES || window.location.hostname === 'localhost') {
    const debugBtn = el("button", {
      className: "btn outline small",
      innerText: "Debug: List ALT",
      title: "List Alt text from current page elements for alias debugging"
    }) as HTMLButtonElement;

    debugBtn.onclick = async () => {
      await debugCurrentPageAliases();
    };

    header.append(debugBtn);
  }

  // Add v2 controls section
  const v2Controls = el("div", { className: "v2-controls" });

  // V2 Re-ingest button
  const reingestV2Btn = el("button", {
    className: "btn primary small",
    innerText: "V2 Re-ingest",
    title: "Full-fidelity capture using v2 system (groups, richtext, exact positioning)"
  }) as HTMLButtonElement;

  reingestV2Btn.onclick = async () => {
    try {
      await ingestMasterV2("DAGz-bFscHI", "cover-dagz-bfsc-hi-v2", "Modern Minimal Cover V2", "all");
    } catch (error) {
      console.error("V2 ingestion failed:", error);
    }
  };

  // V2 Diagnostics button
  const diagnosticsBtn = el("button", {
    className: "btn outline small",
    innerText: "V2 Diagnostics",
    title: "Show v2 template diagnostics and stats"
  }) as HTMLButtonElement;

  diagnosticsBtn.onclick = async () => {
    await getTemplateV2Diagnostics("cover-dagz-bfsc-hi-v2");
  };

  // Legacy re-ingest button (v1)
  const reingestBtn = el("button", {
    className: "btn secondary small",
    innerText: "V1 Re-ingest",
    title: "Legacy capture system (basic elements only)"
  }) as HTMLButtonElement;

  reingestBtn.onclick = async () => {
    try {
      showToast("Starting v1 design ingestion...", "info");
      await triggerCurrentDesignIngestion("cover-dagz-bfsc-hi", "Modern Minimal Cover");
      showToast("V1 design re-ingested successfully! Template updated.", "success");
    } catch (error) {
      console.error("V1 ingestion failed:", error);
      showToast(`V1 ingestion failed: ${error.message}`, "error");
    }
  };

  v2Controls.append(reingestV2Btn, diagnosticsBtn, reingestBtn);
  header.append(v2Controls);

  wrap.append(header);

  const loadingDiv = el("div", { className: "loading", innerText: "Loading templates..." });
  wrap.append(loadingDiv);

  const grid = el("div", { className: "grid" });
  wrap.append(grid);

  // Load cover templates from API
  loadCoverTemplates().then(coverTemplates => {
    loadingDiv.remove();

    // Log current mode for user clarity
    if (USE_SAMPLE_TEMPLATES) {
      console.log('[templates] Sample mode enabled - showing sample templates');
    } else {
      console.log('[templates] Production mode - showing real backend templates only');
      console.log('[templates] To test with samples, add ?sample=1 to URL');
    }

    if (coverTemplates.length === 0) {
      const emptyDiv = el("div", { className: "empty-state" });
      const title = el("div", { className: "empty-title", innerText: "No cover templates available" });
      const message = el("div", {
        className: "empty-message",
        innerText: USE_SAMPLE_TEMPLATES
          ? "No sample templates found. Check backend configuration."
          : "No templates have been ingested yet. Use POST /api/covers/ingestFromMaster to create templates from Master designs."
      });

      if (!USE_SAMPLE_TEMPLATES) {
        // Add sample mode button for testing
        const sampleBtn = el("button", {
          className: "btn primary",
          innerText: "Try Sample Templates",
          title: "Load sample templates for testing"
        }) as HTMLButtonElement;

        sampleBtn.onclick = () => {
          const url = new URL(window.location.href);
          url.searchParams.set('sample', '1');
          window.location.href = url.toString();
        };

        // Add real Master ingestion button
        const ingestBtn = el("button", {
          className: "btn secondary",
          innerText: "Ingest DAGz-bFscHI Master",
          title: "Read the real DAGz-bFscHI Master design and create template"
        }) as HTMLButtonElement;

        ingestBtn.onclick = async () => {
          try {
            await triggerMasterIngestion('DAGz-bFscHI', 'cover-dagz-bfsc-hi', 'Modern Minimal Cover');
            // Reload templates after successful ingestion
            window.location.reload();
          } catch (error) {
            console.error('[templates] Master ingestion failed:', error);
          }
        };

        const helpText = el("div", {
          className: "empty-help",
          innerHTML: 'Or see <a href="#" onclick="console.log(\'Master authoring guide:\\n1. Create Master design in Canva\\n2. Add alias:front__title to Alt text\\n3. Use the Ingest button above\\n4. Apply templates here\')">Master authoring guide</a>'
        });

        emptyDiv.append(title, message, sampleBtn, ingestBtn, helpText);
      } else {
        const backBtn = el("button", {
          className: "btn outline",
          innerText: "Back to Production Mode",
          title: "Disable sample templates"
        }) as HTMLButtonElement;

        backBtn.onclick = () => {
          const url = new URL(window.location.href);
          url.searchParams.delete('sample');
          window.location.href = url.toString();
        };

        emptyDiv.append(title, message, backBtn);
      }

      grid.append(emptyDiv);
      return;
    }

    for (const template of coverTemplates) {
      const card = el("button", { className: "card", title: template.name }) as HTMLButtonElement;
      const th = el("div", { className: "thumb" }, ["📄"]);
      const ti = el("div", { className: "title", innerText: template.name });

      // Create button container for multiple apply options
      const buttonContainer = el("div", { className: "button-container" });

      // V2 Apply button (primary)
      const applyV2Btn = el("button", {
        className: "apply-btn primary",
        innerText: "Apply V2",
        title: "Apply with full-fidelity v2 system (exact positioning, groups, richtext)"
      }) as HTMLButtonElement;

      applyV2Btn.onclick = async (e) => {
        e.stopPropagation();
        await applyTemplateV2Wrapper(template.slug + "-v2", {
          mode: 'exact',
          keepUserContent: true,
          createNewPage: false,
          assetStrategy: 'auto'
        });
      };

      // V1 Apply button (legacy)
      const applyV1Btn = el("button", {
        className: "apply-btn secondary",
        innerText: "Apply V1",
        title: "Apply with legacy v1 system (basic elements)"
      }) as HTMLButtonElement;

      applyV1Btn.onclick = async (e) => {
        e.stopPropagation();
        await applyTemplate(template.slug);
      };

      buttonContainer.append(applyV2Btn, applyV1Btn);
      card.append(th, ti, buttonContainer);

      grid.append(card);
    }
  }).catch(error => {
    loadingDiv.remove();
    const errorDiv = el("div", { className: "error" });
    const title = el("div", { className: "error-title", innerText: "Failed to load templates" });
    const message = el("div", {
      className: "error-message",
      innerText: `Backend error: ${error.message}. Check network and backend configuration.`
    });
    errorDiv.append(title, message);
    grid.append(errorDiv);
    console.error('[templates] Failed to load templates:', error);

    // Log helpful debugging info
    console.log('[templates] Debugging checklist:');
    console.log('1. Check BACKEND_BASE configuration:', CONFIG.BACKEND_BASE);
    console.log('2. Verify /api/covers/list endpoint is accessible');
    console.log('3. Check browser network tab for failed requests');
    console.log('4. For dev mode, try adding ?sample=1 to URL');
  });

  // Show legacy templates only in sample mode (deprecated)
  if (USE_SAMPLE_TEMPLATES && legacyTemplates.length > 0) {
    const legacySection = el("div", { className: "section-title", innerText: "Legacy templates (deprecated - sample mode)" });
    wrap.append(legacySection);

    const legacyGrid = el("div", { className: "grid" });
    for (const t of legacyTemplates) {
      const card = el("button", { className: "card", title: t.title }) as HTMLButtonElement;
      const th = el("div", { className: "thumb" }, [ t.thumb ? el("img",{src:t.thumb}) : "Cover" ]);
      const ti = el("div", { className: "title", innerText: t.title });
      card.append(th, ti);
      card.onclick = async () => {
        if (!t.deeplink || t.deeplink.startsWith("PASTE_")) {
          console.warn("Replace deeplink placeholder with a real Canva deep link.");
          return;
        }
        await requestOpenExternalUrl({ url: t.deeplink });
      };
      legacyGrid.append(card);
    }
    wrap.append(legacyGrid);
  }

  return wrap;
}

/* ============ Photos view ============ */
function PhotosView(): HTMLElement {
  const wrap = el("div");
  wrap.append(el("div",{className:"section-title",innerText:"Photos (upload & sort by EXIF)"}));

  // Photos are initialized in buildSidebar() - no need to call again here



  // Upload section
  const uploader = el("div", { className: "uploader" });
  const fileInput = el("input", { type:"file", accept:"image/*", multiple:true, style:"display:none" }) as HTMLInputElement;
  const drop = el("div", { className:"drop", innerText:"Drop images here or use the button below" });
  const addBtn = el("button", { className:"btn outline", innerText:"Add photos" }) as HTMLButtonElement;

  drop.ondragover = (e)=>{ e.preventDefault(); drop.style.opacity="0.85"; };
  drop.ondragleave = ()=>{ drop.style.opacity="1"; };
  drop.ondrop = async (e)=>{ e.preventDefault(); drop.style.opacity="1";
    const files = Array.from(e.dataTransfer?.files||[]).filter(f=>f.type.startsWith("image/"));
    await addFiles(files);
  };
  addBtn.onclick = ()=> fileInput.click();
  fileInput.onchange = async ()=>{ const files = Array.from(fileInput.files||[]); await addFiles(files); fileInput.value=""; };

  const grid = el("div", { className:"photo-grid" });
  const count = el("div", { className:"meta", innerText:"0 photos selected" });

  async function addFiles(files: File[]) {
    for (const f of files) {
      const objectUrl = URL.createObjectURL(f);
      let date: Date | undefined;
      try {
        const ex = await exifr.parse(f, { tiff:true, ifd0:true, exif:true });
        const exifDate = (ex?.DateTimeOriginal || ex?.CreateDate || ex?.ModifyDate) as Date | undefined;
        date = exifDate ? new Date(exifDate) : undefined;
      } catch {}
      if (!date && f.lastModified) date = new Date(f.lastModified);

      // Measure dimensions early
      const dim = await measureImage(objectUrl).catch(() => ({ w: 1600, h: 1200 }));

      picked.push({ file:f, name:f.name, date, objectUrl, status: "pending", dim });
    }
    redraw();
    // Upload sequentially to avoid too many parallel connections
    for (const p of picked.filter(x => x.status === "pending")) {
      await ensureUploaded(p).catch(() => {});
      redraw();
    }
  }


  // Function to check if a photo is used in the current design
  async function isPhotoInUse(photo: Picked): Promise<boolean> {
    try {
      // Check if photo has a Canva asset reference
      if (!photo.canvaAssetRef && !photo.uploaded?.ref) {
        return false; // Can't be in use if no Canva asset exists
      }

      // Get the asset reference to check
      const assetRef = photo.canvaAssetRef || photo.uploaded?.ref;
      if (!assetRef || assetRef.startsWith('backend-')) {
        return false; // Skip dummy references
      }

      // For now, we'll use a simple heuristic: check if the asset was recently created
      // In a full implementation, we'd query the design content via Canva API
      // This is a placeholder that can be enhanced with actual design content checking
      return false; // Default to not in use for now
    } catch (error) {
      console.warn('[photos] Error checking if photo is in use:', error);
      return false;
    }
  }

  function redraw(){
    picked.sort((a,b)=> (a.date?.getTime()||0)-(b.date?.getTime()||0) || a.name.localeCompare(b.name));
    grid.innerHTML = "";

    // Filter photos based on hide used toggle
    const photosToShow = hideUsedPhotos
      ? picked.filter(p => !p.recentlyUsed)
      : picked;

    for (const p of photosToShow) {
      const card = el("div", { className:"photo-card", draggable:"true" }) as HTMLDivElement;
      const img = el("img", { src:p.objectUrl });
      const cap = el("div", { className:"caption", innerText:`${p.name} • ${formatDate(p.date)}` });

      // Status badge
      const status = el("div", { className: `status ${p.status || "pending"}` });
      status.innerText = p.status === "pending" ? "Pending" :
                        p.status === "uploading" ? "Uploading…" :
                        p.status === "uploaded" ? "Uploaded" :
                        p.status === "error" ? "Error" : "Unknown";
      if (p.error) status.title = p.error;

      // CDN link if available
      const controls = el("div", { className: "card-controls" });
      if (p.publicUrl) {
        const cdnLink = el("a", {
          href: p.publicUrl,
          target: "_blank",
          className: "cdn-link",
          innerText: "↗︎",
          title: "Open CDN URL"
        });
        controls.append(cdnLink);
      }

      // Delete button
      const deleteBtn = el("button", {
        className: "delete-btn",
        innerHTML: "×",
        title: "Delete photo"
      }) as HTMLButtonElement;

      // Prevent all event propagation from delete button
      deleteBtn.addEventListener("click", async (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
        evt.stopImmediatePropagation();

        // Get the photo ID from the photoRecord
        const photoId = p.photoRecord?.id;
        console.log('[photos] Delete button clicked for:', photoId);

        if (!photoId) {
          console.error('[photos] No photo ID found for deletion');
          showInlineHint('Error: Could not identify photo to delete');
          return;
        }

        // Create custom confirmation dialog (since confirm() is blocked in sandboxed environment)
        const confirmDialog = createConfirmationDialog(
          `Delete "${p.name || 'this photo'}"?`,
          'This action cannot be undone.',
          async () => {
            // Add loading state
            deleteBtn.disabled = true;
            deleteBtn.innerHTML = "⋯";
            deleteBtn.style.opacity = "0.5";

            try {
              // Database-first approach: delete from backend first, then update client
              console.log('[photos] Starting database-aware deletion for:', photoId);

              // 1. Delete from backend (database + storage)
              try {
                await deletePhotoFromStorage(photoId);
                console.log('[photos] Backend deletion successful:', photoId);
              } catch (storageError) {
                console.warn('[photos] Backend deletion failed, continuing with client-side:', storageError.message);
                // Continue with client-side deletion even if backend fails
              }

              // 2. Remove from local picked array (immediate UI update)
              const pickedIndex = picked.findIndex(item => item.photoRecord?.id === photoId);
              if (pickedIndex >= 0) {
                picked.splice(pickedIndex, 1);
                console.log('[photos] Removed from picked array:', photoId);
              }

              // 3. Remove from global photos array
              const photoIndex = photos.findIndex(photo => photo.id === photoId);
              if (photoIndex >= 0) {
                photos.splice(photoIndex, 1);
                console.log('[photos] Removed from photos array:', photoId);
              }

              // 4. Update localStorage (persistent client-side storage)
              const localSessionId = getSessionId(); // Use consistent session ID
              const storageKey = `photos_${localSessionId}`;
              localStorage.setItem(storageKey, JSON.stringify(photos));
              await deletePhotoRecord(photoId);

              // 5. Refresh UI immediately
              redraw();

              console.log('[photos] Photo deleted successfully (database-aware):', photoId);
              showInlineHint(`Photo "${p.name || photoId}" deleted`);

            } catch (error) {
              console.error('[photos] Delete failed:', error);
              showInlineHint('Failed to delete photo. Please try again.');
              // Reset button on error
              deleteBtn.disabled = false;
              deleteBtn.innerHTML = "×";
              deleteBtn.style.opacity = "1";
            }
          },
          () => {
            console.log('[photos] Delete cancelled by user');
          }
        );

        // Show the confirmation dialog
        document.body.appendChild(confirmDialog);
      });

      // Prevent other events on delete button
      deleteBtn.addEventListener("mousedown", (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
      });

      deleteBtn.addEventListener("pointerdown", (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
      });

      deleteBtn.addEventListener("dragstart", (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
      });

      controls.append(deleteBtn);

      // Error display
      if (p.error) {
        const errorDiv = el("div", { className: "error-text", innerText: p.error, title: p.error });
        controls.append(errorDiv);
      }

      card.append(img, cap, status, controls);

      // Ensure thumbnail is NOT draggable to avoid native ghost
      img.draggable = false;

      // Add CSS classes for styling
      card.className = "photo-card";
      card.style.cursor = "grab";

      // Prepare photo data for Canva SDK
      const preparePhotoData = (): Photo => {
        // Real dimensions
        const W = p.dim?.w ?? p.uploaded?.width ?? 200;
        const H = p.dim?.h ?? p.uploaded?.height ?? 200;
        const previewSize = { width: Math.min(W, 160), height: Math.min(H, 160) };

        return {
          id: p.name,
          name: p.name,
          file: p.file, // Original file for SDK upload
          publicUrl: p.cdnUrl, // Public URL as fallback
          cdnUrl: p.cdnUrl,
          previewUrl: p.cdnUrl || p.objectUrl,
          previewSize,
          mimeType: p.file?.type,
          dimensions: p.dim,
          canvaAssetRef: p.canvaAssetRef, // Will be set after upload
          canvaAssetId: p.canvaAssetId
        };
      };

      // Create Canva asset if in Canva environment
      const ensureCanvaAsset = async (): Promise<void> => {
        if (!isCanvaEnvironment()) return;

        if (p.canvaAssetRef) {
          console.log('[photos] Photo already has Canva asset:', p.canvaAssetRef);
          return;
        }

        try {
          console.log('[photos] Creating Canva asset for:', p.name);

          // For backend photos, we need to fetch the image and convert to data URL
          // because Canva can't access external CDN URLs directly
          let assetInput: any = {
            title: p.name,
            width: p.dim?.w,
            height: p.dim?.h
          };

          if (p.file && p.file.size > 0) {
            // Use file if available (newly uploaded photos)
            assetInput.file = p.file;
          } else if (p.cdnUrl) {
            // For backend photos, fetch and convert to blob
            console.log('[photos] Fetching image from CDN for Canva asset:', p.cdnUrl);
            const response = await fetch(p.cdnUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch image: ${response.status}`);
            }
            const blob = await response.blob();
            assetInput.file = blob;
          } else {
            throw new Error('No file or CDN URL available for asset creation');
          }

          const asset = await createCanvaImageAsset(assetInput);

          // Store asset reference in photo
          p.canvaAssetRef = asset.assetRef;
          p.canvaAssetId = asset.assetId;

          console.log('[photos] Canva asset created:', asset.assetRef);
        } catch (error) {
          console.error('[photos] Failed to create Canva asset:', error);
          throw error;
        }
      };

      // Enable draggable for Canva SDK
      card.draggable = true;

      // Handle drag start with proper Canva SDK integration
      card.addEventListener("dragstart", async (evt: DragEvent) => {
        console.log('[photos] Drag start for:', p.name);

        // Add visual feedback
        card.classList.add("dragging");

        try {
          // Ensure Canva asset exists
          await ensureCanvaAsset();

          const photoData = preparePhotoData();
          await onPhotoDragStart(evt as any, photoData);
        } catch (error) {
          console.error('[photos] Drag start failed:', error);
          showInlineHint("Failed to start drag. Please try again.");
          evt.preventDefault();
          card.classList.remove("dragging");
        }
      });

      // Handle pointer down for non-drag interactions
      card.addEventListener("pointerdown", (evt: PointerEvent) => {
        if (evt.button !== 0) return; // Only handle left mouse button

        try {
          card.setPointerCapture(evt.pointerId);
        } catch (e) {
          console.warn('[drag] setPointerCapture failed:', e);
        }

        // In Canva environment, prepare asset for potential drag
        if (isCanvaEnvironment()) {
          ensureCanvaAsset().catch(error => {
            console.error('[photos] Failed to prepare Canva asset:', error);
          });
        }
      });

      // Clean up visual feedback
      card.addEventListener("dragend", () => {
        card.classList.remove("dragging");
        console.log('[photos] Drag ended for:', p.name);
      });

      card.addEventListener("pointerup", () => {
        card.classList.remove("dragging");
      });

      card.addEventListener("pointercancel", () => {
        card.classList.remove("dragging");
      });

      card.addEventListener("click", async () => {
        try {
          const up = await ensureUploaded(p);
          const el: any = { type: "image", ref: up.ref }; // no altText
          const canPoint  = features.isSupported(addElementAtPoint);
          const canCursor = features.isSupported(addElementAtCursor);
          if (canPoint) await addElementAtPoint(el);
          else if (canCursor) await addElementAtCursor(el);

          // Mark photo as used (permanently until toggle is turned off)
          p.recentlyUsed = true;

          if (hideUsedPhotos) redraw(); // Immediately hide if toggle is on
          if (verboseLogs) console.log("insert:success", p.name, up.ref);
        } catch (e:any) {
          console.error("insert:error", e);
        }
      });

      grid.append(card);
    }

    // Update count display
    if (hideUsedPhotos) {
      const hiddenCount = picked.length - photosToShow.length;
      count.innerText = `${photosToShow.length} photo${photosToShow.length===1?"":"s"} shown (${hiddenCount} hidden)`;
    } else {
      count.innerText = `${picked.length} photo${picked.length===1?"":"s"} selected`;
    }
  }

  // Register this redraw function globally so initializePhotos can trigger it
  registerRedrawFunction(redraw);

  // Hide used photos toggle
  const hideUsedToggle = el("label", { className: "toggle-switch" });
  const hideUsedCheckbox = el("input", { type: "checkbox" }) as HTMLInputElement;
  const hideUsedLabel = el("span", { innerText: "Hide photos in use" });
  hideUsedToggle.append(hideUsedCheckbox, hideUsedLabel);

  let hideUsedPhotos = false;
  hideUsedCheckbox.onchange = () => {
    hideUsedPhotos = hideUsedCheckbox.checked;
    console.log('[photos] Hide used photos:', hideUsedPhotos);

    // Don't clear the recentlyUsed flags when unchecking
    // This preserves the "used" state for when the toggle is turned back on

    redraw();
  };

  // Clear used photos button (only show when toggle is on)
  const clearUsedBtn = el("button", { className: "btn-small", innerText: "Reset used", style: "display: none" }) as HTMLButtonElement;
  clearUsedBtn.onclick = () => {
    picked.forEach(p => p.recentlyUsed = false);
    console.log('[photos] Cleared all used photo flags');
    redraw();
  };

  // Update clear button visibility when toggle changes
  const originalOnChange = hideUsedCheckbox.onchange;
  hideUsedCheckbox.onchange = (e) => {
    originalOnChange?.call(hideUsedCheckbox, e);
    clearUsedBtn.style.display = hideUsedPhotos ? "inline-block" : "none";
  };

  // Re-sync button
  const resyncBtn = el("button", { className: "btn outline", innerText: "Re-sync" }) as HTMLButtonElement;
  resyncBtn.onclick = async () => {
    resyncBtn.disabled = true;
    resyncBtn.innerText = "Syncing...";
    try {
      await initializePhotos();
      redraw();
    } catch (error) {
      console.error("Re-sync failed:", error);
    } finally {
      resyncBtn.disabled = false;
      resyncBtn.innerText = "Re-sync";
    }
  };

  uploader.append(drop, addBtn, fileInput);
  const controls = el("div", { className: "row", style: "margin-top: 8px; gap: 8px;" });
  controls.append(count, hideUsedToggle, clearUsedBtn, resyncBtn);
  wrap.append(uploader, grid, controls);
  return wrap;
}

/* ============ Layout application functions ============ */
async function applyLayoutAsFrames(preset: { title: string; frames: FrameSpec[] }) {
  const page = await getPagePx();
  const frameResults = [];

  for (const f of preset.frames) {
    const frameEl = buildFrameElementPx(f, page);
    const result = await insertFrameElement(frameEl);
    frameResults.push(result);
  }

  console.log(`Added ${preset.frames.length} frames for "${preset.title}". Drag photos into the frames to fill them.`);

  // Update frame detection after adding frames
  setTimeout(() => {
    updateFramesIndex();
  }, 100);

  return frameResults;
}

// Legacy functions (kept for backward compatibility)
async function getPageSize(): Promise<{ width: number; height: number }> {
  return await getPagePx();
}

async function createFrameAt(x: number, y: number, w: number, h: number, shape: "rect" | "circle" = "rect") {
  const frameElement: any = {
    type: "frame",
    mask: shape === "circle" ? "ellipse" : "rectangle",
    boundingBox: { x, y, width: w, height: h }
  };
  try {
    await addElementAtPoint(frameElement);
  } catch (e) {
    await addElementAtCursor(frameElement);
  }
}

async function applyLayout(preset: LayoutPreset) {
  const page = await getPageSize();
  for (const f of preset.frames) {
    const x = Math.round(f.x * page.width);
    const y = Math.round(f.y * page.height);
    const w = Math.round(f.w * page.width);
    const h = Math.round(f.h * page.height);
    await createFrameAt(x, y, w, h, f.shape || "rect");
  }
  console.log(`Applied layout: ${preset.title} (${preset.frames.length} frames)`);
}

/* ============ Legacy autofill functions ============ */
async function insertImageFullBleed(file: File): Promise<void> {
  // TODO: refine sizing to full-bleed with @canva/design layout calls
  const dataURL = await fileToDataURL(file);
  const wh = await getImageSizeFromObjectUrl(URL.createObjectURL(file));
  const { ref } = await uploadFromFile(file, dataURL, wh);
  const element: any = { type:"image", ref };
  try { await addElementAtPoint(element); } catch { await addElementAtCursor(element); }
}
async function insertTwoUpOnPage(fileA?: File, fileB?: File): Promise<void> {
  if (!fileA) return;
  await insertImageFullBleed(fileA);
  if (fileB) await insertImageFullBleed(fileB);
}
function LayoutsView(): HTMLElement {
  const wrap = el("div");
  wrap.append(el("div", { className: "section-title", innerText: "Autofill layouts (frames)" }));

  const grid = el("div", { className: "grid" });
  for (const preset of LAYOUTS) {
    const card = el("div", { className: "card" });
    const thumb = el("div", { className: "thumb" });
    thumb.innerHTML = layoutPreviewSVG(preset, 160);
    const title = el("div", { className: "title", innerText: preset.title });
    const apply = el("button", { className: "btn", innerText: "Apply layout" }) as HTMLButtonElement;
    apply.onclick = async () => { await applyLayoutAsFrames(preset); };
    card.append(thumb, title, apply);
    grid.append(card);
  }

  // Legacy autofill section
  const legacySection = el("div", { style: "margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--line)" });
  legacySection.append(el("div", { className: "section-title", innerText: "Legacy autofill (direct image placement)" }));
  const row = el("div", { className: "row" });
  const one = el("button", { className: "btn outline", innerText: "Autofill • 1 per page" }) as HTMLButtonElement;
  const two = el("button", { className: "btn outline", innerText: "Autofill • 2 per page" }) as HTMLButtonElement;
  const info = el("div", { className: "meta", innerText: "Uses your uploaded photos (Photos tab), sorted by EXIF." });

  one.onclick = async () => {
    if (!picked.length) {
      console.warn("Add photos in the Photos tab first.");
      return;
    }
    for (const p of picked) await insertImageFullBleed(p.file);
    console.log("Autofill (1 per page) done.");
  };
  two.onclick = async () => {
    if (!picked.length) {
      console.warn("Add photos in the Photos tab first.");
      return;
    }
    for (let i = 0; i < picked.length; i += 2) {
      await insertTwoUpOnPage(picked[i]?.file, picked[i + 1]?.file);
    }
    console.log("Autofill (2 per page) done.");
  };

  row.append(one, two);
  legacySection.append(row, info);

  wrap.append(grid, legacySection);
  return wrap;
}

/* ============ Export view ============ */
function ExportView(): HTMLElement {
  const wrap = el("div");
  wrap.append(el("div",{className:"section-title",innerText:"Export"}));
  const row = el("div",{className:"row"});
  const pdf = el("button",{className:"btn",innerText:"Export PDF"}) as HTMLButtonElement;
  pdf.onclick = async ()=>{ await requestExport({ acceptedFileTypes:["pdf_print","pdf_standard"] }); };
  row.append(pdf);
  wrap.append(row);
  return wrap;
}

/* ============ App shell with tabs ============ */
export function buildSidebar(containerRoot: HTMLElement) {
  injectStyles();

  const root = el("div", { className:"pb-root" });

  // Header
  const header = el("div", {}, [
    el("h2", { innerText: "Photo Book Editor – Sidebar" }),
    el("p",  { innerText: "Switch between Templates, Layouts, Photos and Export." })
  ]);

  // Tabs
  const tabs = el("div", { className:"tabs", role:"tablist" });
  const views: Record<TabKey, HTMLElement> = {
    templates: TemplatesView(),
    layouts:   LayoutsView(),
    photos:    PhotosView(),
    export:    ExportView(),
  };

  // Initialize photos after PhotosView is created and redraw function is registered
  initializePhotos();

  const content = el("div");
  let active: TabKey = (localStorage.getItem(STORAGE_KEY) as TabKey) || "templates";

  function show(tab: TabKey){
    active = tab; localStorage.setItem(STORAGE_KEY, tab);
    content.innerHTML = ""; content.append(views[tab]);
    for (const btn of Array.from(tabs.querySelectorAll<HTMLButtonElement>(".tab-btn"))) {
      btn.setAttribute("aria-selected", String(btn.dataset.tab === tab));
    }
  }

  for(const key of TAB_KEYS){
    const label = key[0].toUpperCase()+key.slice(1);
    const btn = el("button", { className:"tab-btn", innerText: label, role:"tab" }) as HTMLButtonElement;
    btn.dataset.tab = key;
    btn.onclick = ()=> show(key);
    tabs.append(btn);
  }

  // Footer
  const footer = el("div", { className:"pb-footer" }, [
    el("div", { className:"row" }, [
      el("div", { className:"meta", innerText:"Tip: Use the Photos tab to upload, then switch to Layouts for autofill." })
    ])
  ]);

  root.append(header, tabs, content, footer);
  containerRoot.innerHTML = "";
  containerRoot.append(root);

  show(active);

  // Auto-create photobook pages on first load
  setTimeout(() => {
    console.log(`[auto-create] Triggering auto-create from buildSidebar`);
    openDesign({ type: "current_page" }, async (session) => {
      try {
        await createPhotobookPages(session);
      } catch (error) {
        console.error(`[auto-create] Failed to auto-create pages:`, error);
      }
    }).catch(error => {
      console.error(`[auto-create] Failed to open design for auto-create:`, error);
    });
  }, 2000); // 2 second delay to ensure everything is initialized
}


